import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../../assets/styles/colors.css";

function ConfirmModal({
  show,
  title = "Confirm",
  message,
  confirmText = "OK",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  loading = false,
  danger = false,
}) {
  const { t } = useTranslation();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!show) return;
      if (e.key === "Escape") {
        onCancel();
      } else if (e.key === "Enter") {
        onConfirm();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, onCancel, onConfirm]);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 dark:bg-black/70 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="w-[90%] max-w-[400px] rounded-xl border border-slate-200 dark:border-[var(--color-border)] bg-white dark:bg-black shadow-[0_0_20px_var(--color-shadow-primary)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-[var(--color-border)] px-6 py-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-[var(--color-text-white)]">
            {t(title, { defaultValue: title })}
          </h3>
          <button
            className="flex h-8 w-8 items-center justify-center text-xl text-[var(--color-primary)] transition-all hover:border-[var(--color-primary)]"
            onClick={onCancel}
            aria-label="Close"
            disabled={loading}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          <p className="text-sm text-slate-600 dark:text-gray-400 leading-relaxed">
            {message}
          </p>

          {/* Actions */}
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 rounded-lg border border-slate-200 dark:border-[var(--color-border)] text-sm text-slate-600 dark:text-gray-400 transition-all hover:border-[var(--color-primary)]/50 hover:text-[var(--color-primary)] disabled:opacity-50"
            >
              {t(cancelText, { defaultValue: cancelText })}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className={`px-5 py-2.5 rounded-lg border text-sm transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                danger
                  ? "border-[var(--color-error)]/50 bg-[var(--color-error)]/10 text-[var(--color-error)] hover:bg-[var(--color-error)]/20 hover:border-[var(--color-error)] hover:shadow-[0_0_15px_var(--color-error-shadow)]"
                  : "border-[var(--color-primary)] bg-[var(--color-primary)] text-white dark:text-black hover:shadow-[0_0_10px_var(--color-shadow-primary-hover)]"
              }`}
            >
              {loading && (
                <div
                  className={`w-3 h-3 border-2 rounded-full animate-spin ${
                    danger
                      ? "border-[var(--color-error)]/30 border-t-[var(--color-error)]"
                      : "border-white/30 border-t-white dark:border-black/30 dark:border-t-black"
                  }`}
                ></div>
              )}
              {t(confirmText, { defaultValue: confirmText })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConfirmModal;
